import React from 'react';
import { TextV2, LinkV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { useRemoteFeatureFlag } from '@shared/core/featureFlags/remoteFeatureFlags';
import { useCustomPageTelemetry, PriceInfo } from '@apps/guest/packages/layout-engine/widgets/Custom/Custom.telemetry';
import { useTravelPlacesPricingAndBookingDetailsFragment } from '@shared/hooks/useTravelPlacesPricingAndBookingDetails';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { AccommodationCombined } from '../../../../../Accommodations/Accommodation.types';
import { Provider, telemetryDestination } from '../../CompareToPrice';
import { Rate } from '@shared/hooks/useTravelPlacesPriceComparison/useTravelPlacesPriceComparison';

type ProviderLinkProps = {
  accommodation: AccommodationCombined;
  priceToShow: string;
  rates: Rate[];
  onOpenDialog: () => void;
};

export const ProviderLink: React.FC<ProviderLinkProps> = ({ accommodation, priceToShow, rates, onOpenDialog }) => {
  const { value, loading } = useRemoteFeatureFlag('accommodationsPriceComparisonExperiment');
  const { t: accommodationsTrans } = useTranslation('guestSiteCustom');
  const accTrans = accommodationsTrans('accommodations');
  const telemetry = useCustomPageTelemetry();
  const { price, priceInMinorUnits } = useTravelPlacesPricingAndBookingDetailsFragment(accommodation.id, 'accommodationPlace');

  const telemetryHotelInfo = {
    hotelName: accommodation.displayName || '',
    isHotelPreferred: accommodation.isFavorite || false,
    hotelPrice: accommodation.pricePerNight || undefined,
    hotelStrikeThroughPrice: accommodation.strikeoutPricePerNight || undefined,
    type: accommodation.type,
  };

  const getHotelPricesForTelemetry: () => PriceInfo = () => {
    const joyPrice = price || undefined;
    const joyPriceNumber = priceInMinorUnits ?? 0;
    const expedia = rates.find(({ provider }) => provider === Provider.EXPEDIA);
    const booking = rates.find(({ provider }) => provider === Provider.BOOKING);
    const expediaPrice = expedia?.price;
    const bookingPrice = booking?.price;
    const expediaPriceNumber = expedia?.priceInMinorUnits ?? 0;
    const bookingPriceNumber = booking?.priceInMinorUnits ?? 0;

    return {
      joyPrice,
      expediaPrice,
      bookingPrice,
      priceComparisonResult: !joyPriceNumber
        ? 'joypricenotfound'
        : !expediaPriceNumber && !bookingPriceNumber
          ? 'externalpricenotfound'
          : joyPriceNumber < expediaPriceNumber && joyPriceNumber < bookingPriceNumber
            ? 'joywins'
            : 'externalwins',
    };
  };

  const handleOnClickLink = useEventCallback((e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.stopPropagation();
    if (!rates.length) {
      return;
    }
    if (rates.length > 1) {
      e.preventDefault();
      telemetry.openAccommodationsPriceCompareDialog('accommodations', telemetryHotelInfo, getHotelPricesForTelemetry());
      onOpenDialog();
    } else {
      telemetry.accommodationsLinkInteracted(rates[0].url, { ...telemetryHotelInfo, destination: telemetryDestination[rates[0].provider as Provider] });
    }
  });

  if (loading) {
    return null;
  }

  if (value === 'price' && rates.length === 1) {
    return (
      <TextV2
        typographyVariant="body1"
        maxWidth={pxToRem(185)}
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        color="mono10"
        textDecoration="underline"
      >
        {accTrans.comparedToPublicRates({ price: priceToShow })}
      </TextV2>
    );
  }

  return (
    <LinkV2
      typographyVariant="body1"
      maxWidth={pxToRem(185)}
      whiteSpace="nowrap"
      overflow="hidden"
      textOverflow="ellipsis"
      zIndex={1}
      _activeLink={{ color: 'mono10' }}
      _visited={{ color: 'mono10' }}
      _active={{ color: 'mono10' }}
      _pressed={{ color: 'mono10' }}
      color="mono10"
      textDecoration="underline"
      target="_blank"
      onClick={handleOnClickLink}
      {...(rates.length === 1
        ? {
            href: rates[0].url,
          }
        : {})}
    >
      {accTrans.comparedToPublicRates({ price: priceToShow })}
    </LinkV2>
  );
};
